/**
 * KitElement — the element base (RFC §4).
 *
 * MVVM on IoC: light-DOM custom elements whose presentation logic lives in a
 * plain, constructor-injected ViewModel. Rendering is pure (`render(vm)` →
 * lit-html TemplateResult) and driven by the slice-1 scheduler: any change to a
 * signal in `vm.renderDeps` marks the element dirty; the scheduler calls
 * `flushRender()` once per flush, which does `litRender(this.render(vm), this)`
 * — one lit call per flush per element, part-diffing preserves focus/scroll.
 *
 * Lifecycle (§4): connectedCallback → resolve KitContext (context-request,
 * synchronously — providers must be already-connected ancestors) →
 * createViewModel(ctx) → vm.attach() → subscribe renderDeps via effect() (its
 * initial run schedules the first render) → install the ONE delegated action
 * listener. disconnectedCallback disposes effects, VM teardown, context
 * subscriptions, and the action listener. A change to the `urn` attribute while
 * connected REBINDS the VM (dispose old → create new → re-subscribe → render).
 *
 * DOM-free import guarantee: this module (and the package root) can be imported
 * without DOM globals — `HTMLElement` is resolved lazily with a throwing stub
 * fallback, and lit-html touches `document` only at render time. Constructing
 * or connecting a KitElement still requires a real DOM, by design.
 *
 * Subclasses that observe extra attributes must extend, not replace:
 * `static observedAttributes = [...KitElement.observedAttributes, "variant"]`.
 */

import { effect, type ReadonlySignal } from "./signal";
import { markDirty, type FlushPolicy, type RenderTarget } from "./scheduler";
import { createKitContext, type ContextToken, type KitContext } from "./context";
import { installActionDelegate, type ActionMap } from "./actions";
import { litRender, type TemplateResult } from "../render/html";
import type { Dispose, ViewModel } from "./viewmodel";

/** The platform identity vocabulary: `urn:career:<type>:<id>`. */
export type Urn = `urn:${string}`;

/** Lazily-resolved base so importing this module never requires DOM globals. */
const HTMLElementBase: typeof HTMLElement =
  (globalThis as { HTMLElement?: typeof HTMLElement }).HTMLElement ??
  (class {
    constructor() {
      throw new Error(
        "[web-kit] KitElement requires a DOM environment (HTMLElement is not defined)."
      );
    }
  } as unknown as typeof HTMLElement);

export abstract class KitElement<VM extends ViewModel = ViewModel>
  extends HTMLElementBase
  implements RenderTarget
{
  static readonly observedAttributes: readonly string[] = ["urn"];

  /** Scheduler lane for this element ("microtask" default; "raf" opt-in, §3a). */
  readonly flushPolicy?: FlushPolicy;

  #ctx: KitContext | null = null;
  #vm: VM | null = null;
  #stopRenderEffect: Dispose | null = null;
  #detachVm: Dispose | null = null;
  #removeActions: Dispose | null = null;
  #connected = false;

  /** URN this element is bound to (attribute-reflected). Null for layout elements. */
  get urn(): Urn | null {
    const value = this.getAttribute("urn");
    return value !== null && value.startsWith("urn:") ? (value as Urn) : null;
  }

  set urn(value: Urn | null) {
    if (value === null) this.removeAttribute("urn");
    else this.setAttribute("urn", value);
  }

  /** This element's IoC context (lazily created, host-bound). */
  protected get context(): KitContext {
    this.#ctx ??= createKitContext(this);
    return this.#ctx;
  }

  /** IoC lookup, resolved via context-request events walking the DOM tree (§4a). */
  protected inject<T>(token: ContextToken<T>): T {
    return this.context.inject(token);
  }

  /** The bound ViewModel. Throws before connect / after disconnect. */
  protected get vm(): VM {
    if (!this.#vm) {
      throw new Error(
        `[web-kit] <${this.localName}> has no ViewModel — it is not connected.`
      );
    }
    return this.#vm;
  }

  /** Created once per connection (and per urn rebind) via the app's wiring. */
  protected abstract createViewModel(ctx: KitContext): VM;

  /** Pure: renders from the VM. Scheduled when any signal in vm.renderDeps changes. */
  protected abstract render(vm: VM): TemplateResult;

  /** Action handlers, keyed by data-act verb. One delegated listener on the host. */
  protected actions?(vm: VM): ActionMap;

  connectedCallback(): void {
    this.#connected = true;
    this.#bind();
    this.#removeActions = installActionDelegate(this, () =>
      this.#vm ? this.actions?.(this.#vm) : undefined
    );
  }

  disconnectedCallback(): void {
    this.#connected = false;
    this.#removeActions?.();
    this.#removeActions = null;
    this.#unbind();
    this.#ctx?.dispose();
    this.#ctx = null;
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    if (name !== "urn" || oldValue === newValue || !this.#connected) return;
    // urn rebind (§4): dispose the old VM, create + wire a fresh one.
    this.#unbind();
    this.#bind();
  }

  /** Scheduler entry point: one lit-html render into the light DOM (§3a). */
  flushRender(): void {
    if (!this.#connected || !this.#vm) return; // marked dirty, then disconnected
    litRender(this.render(this.#vm), this);
  }

  #bind(): void {
    const vm = this.createViewModel(this.context);
    this.#vm = vm;
    this.#detachVm = vm.attach?.() ?? null;
    // The effect's immediate initial run schedules the FIRST render;
    // every subsequent dep change marks dirty again (scheduler dedupes).
    this.#stopRenderEffect = effect(
      [...vm.renderDeps] as ReadonlySignal<unknown>[],
      () => {
        markDirty(this);
      }
    );
  }

  #unbind(): void {
    this.#stopRenderEffect?.();
    this.#stopRenderEffect = null;
    this.#detachVm?.();
    this.#detachVm = null;
    this.#vm = null;
  }
}
